import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateConstraint, useCreateMealExclusion } from '@/hooks/useConstraints';
import type { ConstraintType, ConstraintSeverity } from '@/types/api';

type CaptureMode = 'exclusion' | 'constraint';

interface ConstraintCaptureFormProps {
  userId: number;
  userName: string;
  mealId: number;
  mealName: string;
  onSaved: () => void;
  onCancel: () => void;
}

export function ConstraintCaptureForm({
  userId,
  userName,
  mealId,
  mealName,
  onSaved,
  onCancel,
}: ConstraintCaptureFormProps) {
  const { t } = useTranslation();
  const createConstraint = useCreateConstraint();
  const createExclusion = useCreateMealExclusion();

  const [mode, setMode] = useState<CaptureMode>('exclusion');
  const [reason, setReason] = useState('');
  const [constraintType, setConstraintType] = useState<ConstraintType>('dislike');
  const [value, setValue] = useState('');
  const [severity, setSeverity] = useState<ConstraintSeverity>('preference');

  const isPending = createConstraint.isPending || createExclusion.isPending;
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (mode === 'exclusion') {
        await createExclusion.mutateAsync({
          userId,
          mealId,
          reason: reason || undefined,
        });
      } else {
        if (!value.trim()) return;
        await createConstraint.mutateAsync({
          userId,
          constraintType,
          value: value.trim(),
          severity,
        });
      }

      onSaved();
    } catch {
      setError(t('errors.generic'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {t('constraint.captureTitle')}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {t('constraint.captureFor', { name: userName, meal: mealName })}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Mode selection */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                checked={mode === 'exclusion'}
                onChange={() => setMode('exclusion')}
                className="text-blue-600"
              />
              <span className="text-sm">{t('constraint.mealExclusion')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                checked={mode === 'constraint'}
                onChange={() => setMode('constraint')}
                className="text-blue-600"
              />
              <span className="text-sm">
                {t('constraint.ingredientConstraint')}
              </span>
            </label>
          </div>

          {mode === 'exclusion' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('constraint.reason')}
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('constraint.reasonPlaceholder')}
                className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('constraint.constraintType')}
                </label>
                <select
                  value={constraintType}
                  onChange={(e) =>
                    setConstraintType(e.target.value as ConstraintType)
                  }
                  className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="allergy">{t('constraint.allergy')}</option>
                  <option value="dislike">{t('constraint.dislike')}</option>
                  <option value="diet">{t('constraint.diet')}</option>
                  <option value="medical">{t('constraint.medical')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('constraint.value')}
                </label>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={t('constraint.valuePlaceholder')}
                  className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('constraint.severity')}
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="severity"
                      checked={severity === 'strict'}
                      onChange={() => setSeverity('strict')}
                      className="text-blue-600"
                    />
                    <span className="text-sm">{t('constraint.strict')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="severity"
                      checked={severity === 'preference'}
                      onChange={() => setSeverity('preference')}
                      className="text-blue-600"
                    />
                    <span className="text-sm">
                      {t('constraint.preference')}
                    </span>
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isPending}
              className="flex-1 text-sm font-medium py-2 px-4 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              {t('app.cancel')}
            </button>
            <button
              type="submit"
              disabled={isPending || (mode === 'constraint' && !value.trim())}
              className="flex-1 text-sm font-medium py-2 px-4 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isPending ? t('app.saving') : t('app.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
